/**
 * DiscrepancyContent.tsx — The shared discrepancy-resolution panel (inline
 * expansion under a line-item row). Left: severity + detail + CME-vs-Listino
 * compare cards. Right: status buttons + comment thread. Wired to the live
 * tender-api: the discrepancy brief comes from the line item; the comment
 * thread is loaded via useDiscrepancy; status + comment are real mutations.
 */
import { useState } from "react";

import { I } from "../../components/icons";
import {
  useAddDiscrepancyComment,
  useDiscrepancy,
  useSetDiscrepancyStatus,
  type AuditComment,
  type AuditLineItem,
} from "../../lib/tenderApi";
import {
  discrepancyTypeLabel,
  statusBg,
  statusFg,
  statusFromBackend,
  statusToBackend,
  type ResolutionStatus,
} from "./helpers";

const RESOLUTION_OPTIONS: ResolutionStatus[] = ["Risolto", "In revisione", "Non risolto"];

/** Short "HH:MM" or date label for a comment timestamp. */
function commentTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Two-letter initials from an Auth0 sub (best-effort; the backend stores no name). */
function authorInitials(sub: string): string {
  const tail = sub.includes("|") ? sub.slice(sub.indexOf("|") + 1) : sub;
  return tail.slice(0, 2).toUpperCase();
}

function CompareCard({
  tag,
  value,
  delta,
  src,
  warn,
}: {
  tag: string;
  value: string;
  delta?: string | null;
  src: string;
  warn?: boolean;
}): JSX.Element {
  return (
    <div
      style={{
        padding: 10,
        borderRadius: 8,
        background: warn ? "rgba(184,115,12,0.07)" : "var(--c-bg-elev)",
        border: `1px solid ${warn ? "rgba(184,115,12,0.30)" : "var(--c-line)"}`,
      }}
    >
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 600,
          color: warn ? "var(--c-warn)" : "var(--c-ink-3)",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        {tag}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4, fontFamily: "var(--font-mono)" }}>
        {value}
        {delta && (
          <span style={{ marginLeft: 6, fontSize: 11, color: "var(--c-warn)", fontWeight: 600 }}>
            {delta}
          </span>
        )}
      </div>
      <div style={{ fontSize: 11, color: "var(--c-ink-3)", marginTop: 4 }}>{src}</div>
    </div>
  );
}

function DiscrepancyCompare({ item }: { item: AuditLineItem }): JSX.Element | null {
  const d = item.discrepancy;
  if (!d) return null;
  if (d.type === "price_mismatch") {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <CompareCard tag="CME (€/u)" value={`€ ${item.unitPrice.toFixed(2)}`} src="Computo CME" />
        <CompareCard
          tag="Listino regionale"
          value={d.compareValue ?? "—"}
          delta={d.compareDelta}
          src={d.compareSource ?? ""}
          warn
        />
      </div>
    );
  }
  // desc_mismatch (and any qty_mismatch fallback)
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
      <CompareCard
        tag="Descrizione CME"
        value={item.description.slice(0, 60) + (item.description.length > 60 ? "…" : "")}
        src="Computo CME"
      />
      <CompareCard
        tag="Listino regionale"
        value={d.compareValue ?? "—"}
        src={d.compareSource ?? ""}
      />
    </div>
  );
}

export function DiscrepancyContent({
  item,
  projectId,
}: {
  item: AuditLineItem;
  projectId: string;
}): JSX.Element {
  const [draft, setDraft] = useState("");
  const d = item.discrepancy;
  const discId = d?.id ?? null;

  const detailQuery = useDiscrepancy(discId);
  const setStatus = useSetDiscrepancyStatus(projectId);
  const addComment = useAddDiscrepancyComment();

  const status: ResolutionStatus = d ? statusFromBackend(d.status) : "Non risolto";
  const comments: AuditComment[] = detailQuery.data?.comments ?? [];
  const sevPill = d?.severity === "err" ? "pill-err" : "pill-warn";
  const sevLabel = d?.severity === "err" ? "Critica" : "Da rivedere";

  function submit(): void {
    if (draft.trim() && discId) {
      addComment.mutate({ discrepancyId: discId, text: draft.trim() });
      setDraft("");
    }
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 0 }}>
      {/* Left: discrepancy details */}
      <div style={{ padding: "16px 20px", borderRight: "1px solid rgba(184,115,12,0.20)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <span className={`pill ${sevPill}`}>
            <I.Alert size={10} />
            {sevLabel}
          </span>
          {d && (
            <span style={{ fontSize: 12, color: "var(--c-ink-2)", fontWeight: 500 }}>
              {discrepancyTypeLabel(d.type)}
            </span>
          )}
          <span
            className="t-mono"
            style={{ marginLeft: "auto", fontSize: 11, color: "var(--c-ink-3)" }}
          >
            {item.code ?? item.id.slice(0, 8)}
          </span>
        </div>

        <div
          style={{
            background: "var(--c-bg-elev)",
            border: "1px solid rgba(184,115,12,0.24)",
            borderRadius: 8,
            padding: 12,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "var(--c-ink-3)",
              marginBottom: 6,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            Dettaglio
          </div>
          <div style={{ fontSize: 13, color: "var(--c-ink)" }}>{d?.note}</div>
        </div>

        <DiscrepancyCompare item={item} />
      </div>

      {/* Right: status + comments */}
      <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "var(--c-ink-3)",
              marginBottom: 6,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            Stato risoluzione
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {RESOLUTION_OPTIONS.map((s) => (
              <button
                key={s}
                disabled={!discId || setStatus.isPending}
                onClick={() =>
                  discId && setStatus.mutate({ discrepancyId: discId, status: statusToBackend(s) })
                }
                className="btn btn-sm"
                style={{
                  background: status === s ? statusBg(s) : "var(--c-bg-elev)",
                  color: status === s ? statusFg(s) : "var(--c-ink-2)",
                  borderColor: status === s ? statusFg(s) + "40" : "var(--c-line-strong)",
                  fontWeight: status === s ? 600 : 500,
                }}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "var(--c-ink-3)",
              marginBottom: 6,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            Thread commenti
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 10 }}>
            {detailQuery.isLoading && (
              <div style={{ fontSize: 12, color: "var(--c-ink-3)" }}>Caricamento commenti…</div>
            )}
            {detailQuery.isSuccess && comments.length === 0 && (
              <div style={{ fontSize: 12, color: "var(--c-ink-3)" }}>Nessun commento.</div>
            )}
            {comments.map((c) => (
              <div key={c.id} style={{ display: "flex", gap: 8 }}>
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 11,
                    flexShrink: 0,
                    background: "var(--c-brand-soft)",
                    color: "var(--c-brand-ink)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10,
                    fontWeight: 700,
                  }}
                >
                  {authorInitials(c.authorSub)}
                </div>
                <div style={{ flex: 1, fontSize: 12.5, lineHeight: 1.5 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                    <span style={{ fontWeight: 600 }}>{c.authorSub}</span>
                    <span style={{ color: "var(--c-ink-4)", fontSize: 11 }}>
                      · {commentTime(c.createdAt)}
                    </span>
                  </div>
                  <div style={{ color: "var(--c-ink)" }}>{c.text}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <input
              className="input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Aggiungi una nota…"
              disabled={!discId || addComment.isPending}
              style={{ flex: 1, height: 30, fontSize: 12 }}
              onKeyDown={(e) => {
                if (e.key === "Enter") submit();
              }}
            />
            <button
              className="btn btn-sm"
              onClick={submit}
              disabled={!discId || addComment.isPending}
            >
              <I.Send size={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
